<template>
  <v-container class="help-section">
    <div class="section-header text-center mb-12">
      <h2 class="section-title">Need Help?</h2>
      <p class="section-subtitle">We're here to support your automation journey</p>
    </div>
    
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="help-card" elevation="0">
          <v-card-text class="pa-0">
            <v-row no-gutters>
              <v-col 
                v-for="(resource, index) in helpResources" 
                :key="resource.id"
                cols="12" 
                sm="6" 
                md="4"
                class="resource-col"
              >
                <div 
                  class="resource-item"
                  :style="{ animationDelay: `${index * 100}ms` }"
                  @click="handleResourceClick(resource)"
                >
                  <div class="resource-icon">
                    <v-icon :color="resource.color" size="32">
                      {{ resource.icon }}
                    </v-icon>
                  </div>
                  <h4 class="resource-title">{{ resource.title }}</h4>
                  <p class="resource-description">{{ resource.description }}</p>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Contact Support CTA -->
    <v-row justify="center" class="mt-8">
      <v-col cols="12" md="6" class="text-center">
        <div class="support-cta">
          <v-icon size="48" color="primary" class="mb-4">mdi-headset</v-icon>
          <h3 class="text-h5 mb-3">Still have questions?</h3>
          <p class="text-body-1 mb-4">Our support team is ready to help you succeed</p>
          <v-btn
            large
            color="primary"
            @click="openChat"
          >
            <v-icon left>mdi-email</v-icon>
            Chat with us now
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Redirect } from '@shopify/app-bridge/actions';
import { isShopifyEmbedded } from '@shopify/app-bridge/utilities';

export default {
  name: 'HelpResources',
  
  data() {
    return {
      helpResources: [
        {
          id: 'documentation',
          title: 'Documentation',
          description: 'Detailed guides and tutorials',
          icon: 'mdi-book-open-page-variant',
          color: 'primary',
          action: 'external',
          url: 'https://help.assisty.ai/en/article/automated-purchase-order-user-guide-1fcs3e1/?bust=1750905120519'
        },
        {
          id: 'video-tutorials',
          title: 'Video Tutorials',
          description: 'Step-by-step walkthroughs',
          icon: 'mdi-play-circle',
          color: 'error',
          action: 'external',
          url: 'https://app.arcade.software/share/biJuO4WFrC800NKADkqN'
        },
        {
          id: 'faq',
          title: 'FAQs',
          description: 'Common questions answered',
          icon: 'mdi-help-circle',
          color: 'warning',
          action: 'external',
          url: 'https://help.assisty.ai/en/category/faqs-2471lc/'
        },
      ]
    };
  },
  
  methods: {
    handleResourceClick(resource) {
      // For Shopify embedded apps, use App Bridge to handle external navigation
      if (isShopifyEmbedded() && this.$root.app) {
        // Use Shopify App Bridge Redirect action for external links
        Redirect.create(this.$root.app).dispatch(Redirect.Action.REMOTE, resource.url);
      } else {
        // Fallback for non-embedded context
        window.open(resource.url, '_blank');
      }
    },
    
    contactSupport() {
      // In a real app, this would open a support ticket or email
      window.location.href = 'mailto:support@example.com?subject=Automated PO Help';
    },

    openChat() {
      const openChatMessage = '';
      // @ts-ignore: Unreachable code error
      this.$crisp.do("chat:open");
      // @ts-ignore: Unreachable code error
      // this.$crisp.do('message:send', ['text', this.$t(`chat_with_us.${this.defaultMessage}`)]);
      this.$crisp.do("message:send", ["text", openChatMessage]);
    }
    
  }
};
</script>

<style scoped>
.help-section {
  padding: 80px 0;
  background: linear-gradient(to bottom, #ffffff 0%, #f5f5f5 100%);
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #546e7a;
  font-weight: 300;
}

.help-card {
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: white;
}

.resource-col {
  position: relative;
}

.resource-col:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60%;
  background: rgba(0, 0, 0, 0.05);
}

@media (max-width: 960px) {
  .resource-col::after {
    display: none;
  }
}

.resource-item {
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.6s ease-out forwards;
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.resource-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(0, 0, 0, 0.02) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.resource-item:hover::before {
  opacity: 1;
}

.resource-item:hover {
  background: rgba(0, 0, 0, 0.02);
  transform: translateY(-4px);
}

.resource-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.resource-item:hover .resource-icon {
  transform: scale(1.1);
  background: rgba(0, 0, 0, 0.08);
}

.resource-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.resource-description {
  font-size: 0.95rem;
  color: #546e7a;
  margin: 0;
}

.support-cta {
  padding: 60px 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.support-cta::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

.support-cta h3,
.support-cta p {
  color: white;
  position: relative;
  z-index: 1;
}

.support-cta .v-btn {
  position: relative;
  z-index: 1;
  background: white !important;
  color: #667eea !important;
}

.support-cta .v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 960px) {
  .help-section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .resource-item {
    padding: 30px 15px;
  }
  
  .support-cta {
    padding: 40px 20px;
  }
}
</style> 